import { util } from '../../util'
import { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from 'react'

export const useTable = (
    order,
    orderBy,
    page,
    size,
    tableList,
    headCells,
    count,
    applied_filters = null,
    options = null
) => {
    const initialState = {
        deletingRow: [],
        editRow: {},
        order: order,
        orderBy: orderBy,
        pageNo: page,
        pageCount: 1,
        rowsPerPage: size,
        selected: [],
        showDelete: false,
        toggleDelete: false,
        toggleEdit: {},
        publishContent: '',
        filters: null,
        headCells,
    }

    const stateTypes = {
        CHANGE_PAGE: 'CHANGE_PAGE',
        CHANGE_ROWS_PER_PAGE: 'CHANGE_ROWS_PER_PAGE',
        CLEAR_STATE_FIELD: 'CLEAR_STATE_FIELD',
        DELETE_ROW_ACTION: 'DELETE_ROW_ACTION',
        FILTER_SELECT_ALL: 'FILTER_SELECT_ALL',
        PUSH_SELECT_ONE: 'PUSH_SELECT_ONE',
        PUSH_SELECT_ALL: 'PUSH_SELECT_ALL',
        REQUEST_SORT: 'REQUEST_SORT',
        SELECT_ALL_ROW: 'SELECT_ALL_ROW',
        TOGGLE_COLUMN_MODAL: 'TOGGLE_COLUMN_MODAL',
        TOGGLE_SHOW_DELETE: 'TOGGLE_SHOW_DELETE',
        UPDATE_STATE_FIELD: 'UPDATE_STATE_FIELD',
        UPDATE_MULTIPLE_FIELDS: 'UPDATE_MULTIPLE_FIELDS',
    }

    const reducer = useCallback((state, action) => {
        const { type, payload, field } = action
        const { shallowHelper } = util

        switch (type) {
            case stateTypes.CHANGE_ROWS_PER_PAGE:
                return shallowHelper(state, {
                    rowsPerPage: payload.rowsPerPage,
                    pageCount: payload.pageCount,
                })

            case stateTypes.CLEAR_STATE_FIELD:
                return shallowHelper(state, { [field]: initialState[field] })

            case stateTypes.FILTER_SELECT_ALL:
                return shallowHelper(state, {
                    selected: state.selected.filter(row => row._id !== payload),
                })

            case stateTypes.PUSH_SELECT_ONE:
                return shallowHelper(state, { selected: [payload] })

            case stateTypes.PUSH_SELECT_ALL:
                return shallowHelper(state, { selected: state.selected.concat(payload) })

            case stateTypes.REQUEST_SORT:
                return shallowHelper(state, { order: payload.order, orderBy: payload.orderBy })

            case stateTypes.TOGGLE_COLUMN_MODAL:
                return shallowHelper(state, { showColumnModal: payload })

            case stateTypes.TOGGLE_SHOW_DELETE:
                return shallowHelper(state, { showDelete: !state.showDelete })

            case stateTypes.UPDATE_STATE_FIELD:
                return shallowHelper(state, { [field]: payload })

            case stateTypes.UPDATE_MULTIPLE_FIELDS:
                return shallowHelper(state, payload)

            default:
                return state
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /*
        tableState: State of an Object
        setTableState: dispatch function used to update state of a table component
     */
    const [tableState, setTableState] = useReducer(reducer, initialState)
    const { filters, selected, rowsPerPage, pageNo } = tableState

    useEffect(() => {
        let updatedCount = Math.ceil(count / size)
        if (updatedCount === 0) {
            updatedCount = 1
        }

        setTableState({
            type: stateTypes.UPDATE_MULTIPLE_FIELDS,
            payload: { pageCount: updatedCount, pageNo: page },
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, page, size])

    useEffect(() => {
        applied_filters !== null && !applied_filters.toJS && parseAppliedFilters()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applied_filters])

    useEffect(() => {
        if (options && !!options.onTableDataChange) {
            onTableChange()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowsPerPage, pageNo, tableState.orderBy, tableState.order, filters])

    const onTableChange = () => {
        if (!filters) {
            return null
        }

        if (selected.length > 0) {
            clearSelectAll()
        }

        /* If rows per page changes, or if any filter is applied after moving to a different page,
        then the current page should fallback to 1 */
        const updatedPage =
            size !== rowsPerPage || (Object.keys(filters).length !== 0 && pageNo === page)
                ? 1
                : pageNo

        if (updatedPage !== pageNo) {
            /* this will updates the page and calls the fetch promos again */
            pageChangeHandler(updatedPage)
        } else {
            options.onTableDataChange({
                search: filters,
                sort: { [tableState.orderBy]: tableState.order },
                size: rowsPerPage,
                pageNo: updatedPage,
            })
        }
    }

    // const fixedEndCol = useRef({}) //ref pointing to end column
    const fixedStartCol = useRef({}) //ref pointing to start column
    const tableRowRef = useRef({}) //ref to pointing to table row and controls the height of the table
    const checkboxRef = useRef({})
    const tableInnerRef = useRef({}) //ref pointing to inner div which is wrapping the table
    const headCellsRef = useRef({}) //ref pointing to all the table heads
    const tableOuterRef = useRef({}) //ref pointing to outer div which is wrapping the table

    let throttleSetTableWidths = null

    /**
     * Use Layout Effect will execute before before DOM Render and DOM Painting.
     */
    useLayoutEffect(() => {
        /* Checking conditions in order to proceed responsive table. */
        if (options && options.scrollable && tableOuterRef.current && tableInnerRef.current) {
            _responsiveTableDims()
            window.addEventListener('resize', _throttleEffect)
        }
        window.dispatchEvent(new Event('resize'))
        return () => window.removeEventListener('resize', _throttleEffect)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headCells, page, tableState.rowsPerPage, tableList])

    /**
     * Using a throttle function in order to resist the unnecessary rendering.
     * For resize event handler
     */
    const _throttleEffect = () => {
        clearTimeout(throttleSetTableWidths)
        throttleSetTableWidths = setTimeout(() => _responsiveTableDims(), 300)
    }

    const _responsiveTableDims = () => {
        const tableWidth = tableOuterRef.current.offsetWidth
        /* assigning outer with to inner width
        tableInnerRef.current.style.width = tableWidth
        /**
         * Iterating the heading cells in order to apply widths
         * table body cells will follow table head cells widths
         */
        headCells.forEach(cell => {
            Object.keys(headCellsRef.current).forEach(ref => {
                if (ref === cell.label && headCellsRef.current[ref]) {
                    const length = (parseInt(cell.width) / 100) * parseInt(tableWidth)
                    headCellsRef.current[ref].style.width = `${length}px`
                    // headCellsRef.current[ref].style.width = `${length + 24}px`

                    // if (cell.label === options.fixedStart)
                    //     if (cell.label === options.fixedEnd)
                    //         _setTableCellWidth(fixedStartCol, length)

                    // if (isScrollable) _setTableCellWidth(fixedEndCol, length)

                    /* Adding extra width when cells are scrollable so that we will eliminate remaining space */

                    /**
                     * if a table reaches the max number of columns. we need to fixed the
                     * first two cols and scroll the remaining table
                     */
                    // if (cell.label === options.fixedEnd) {
                    //     //Moving the table to right. First two cols will have a abs position.
                    //     tableInnerRef.current.style.marginRight = `${length + 24}px`
                    //     _setTableCellWidth(fixedEndCol, length + 24)
                    // }
                    // console.log(cell, length)
                    if (cell.label === options.fixedStart) {
                        headCellsRef.current[ref].style.width = `${length + 24}px`
                        tableInnerRef.current.style.marginLeft = `${length + 64 + 24}px`
                        tableList.length && _setTableCellDims(fixedStartCol, length + 24)
                    }

                    // if (options && options.scrollable) {
                    //     tableInnerRef.current.style.marginLeft = '0px'
                    //     tableInnerRef.current.style.marginRight = '0px'
                    // }
                }
            })
        })
    }

    /**
     * In order to have a fixed cols we are making the first columns and last col absolute.
     * Now there are not following the table properties. so we need to manually
     * set the width and height for the table cells(start cols and end col)
     * @param ref {Ref} fixed columns refs (fixedStartCol, fixedEndCol)
     * @param length{Number} length of the each width
     * @private
     */
    const _setTableCellDims = (ref, length) => {
        !!ref.current &&
            Object.keys(ref.current).forEach(node => {
                if (ref.current[node] !== null) {
                    const rowHeight = tableRowRef.current[node].offsetHeight
                    ref.current[node].style.width =
                        ref.current[node] !== null ? `${length}px` : 'null'

                    ref.current[node].style.height = `${rowHeight}px`
                    checkboxRef.current[node].style.height = `${rowHeight}px`
                    checkboxRef.current[node].style.width = '80px'
                }
            })
    }

    const onTableHeadCellsChange = useCallback(headers => {
        setTableState({
            type: stateTypes.UPDATE_STATE_FIELD,
            field: 'headCells',
            payload: headers,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const checkboxHandler = useCallback((e, row, isSingleCheck = false) => {
        if (e.target.checked) {
            setTableState({
                type: !isSingleCheck ? stateTypes.PUSH_SELECT_ALL : stateTypes.PUSH_SELECT_ONE,
                payload: row,
            })
        } else {
            setTableState({
                type: stateTypes.FILTER_SELECT_ALL,
                payload: row._id,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const clearSelectAll = useCallback(
        () => {
            console.log('clearSelectAll')
            setTableState({ type: stateTypes.CLEAR_STATE_FIELD, field: 'selected' })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    const handleRequestSort = useCallback(
        id => {
            const isAsc = tableState.orderBy === id && tableState.order === 'asc'
            setTableState({
                type: stateTypes.REQUEST_SORT,
                payload: {
                    order: isAsc ? 'desc' : 'asc',
                    orderBy: id,
                },
            })
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tableState.order, tableState.orderBy]
    )

    const onSelectAllHandler = useCallback(
        e => {
            if (e.target.checked) {
                setTableState({
                    type: stateTypes.UPDATE_STATE_FIELD,
                    field: 'selected',
                    payload: tableList,
                })
            } else {
                clearSelectAll()
            }
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tableList]
    )

    const rowsPerPageHandler = useCallback(value => {
        setTableState({
            type: stateTypes.UPDATE_STATE_FIELD,
            field: 'rowsPerPage',
            payload: value,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const clearStateField = useCallback(field => {
        setTableState({
            type: stateTypes.CLEAR_STATE_FIELD,
            field,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setEditRow = useCallback(editingRow => {
        setTableState({
            type: stateTypes.UPDATE_STATE_FIELD,
            field: 'editRow',
            payload: editingRow,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const pageChangeHandler = useCallback(
        currentPage => {
            setTableState({
                type: stateTypes.UPDATE_STATE_FIELD,
                field: 'pageNo',
                payload: currentPage,
            })
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tableState.pageNo, tableState.rowsPerPage]
    )

    const togglePublishContentModal = useCallback(value => {
        setTableState({
            type: stateTypes.UPDATE_STATE_FIELD,
            field: 'publishContent',
            payload: value,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const parseAppliedFilters = () => {
        const searchFilters = {}
        applied_filters.forEach(filter => {
            if (filter.type === 'range') {
                if (filter.start_value) {
                    searchFilters['start_date'] = filter.start_value
                }
                if (filter.end_value) {
                    searchFilters['end_date'] = filter.end_value
                }
            } else if (filter.type === 'option') {
                filter.post_value.length &&
                    filter.post_value.forEach(({ key, ...rest }) => {
                        searchFilters[key] = searchFilters[key]
                            ? searchFilters[key].concat({ ...rest })
                            : [{ ...rest }]
                    })
            } else if (filter.key === 'state') {
                if (filter.display_value.includes('National')) {
                    searchFilters['is_national'] = true
                    const index = filter.post_value.indexOf('all')
                    filter.post_value.splice(index, 1)
                }
                if (filter.post_value.length > 0) {
                    searchFilters[filter.key] = filter.post_value
                }
            } else if (
                (filter.post_value && filter.post_value.length > 0) ||
                util.isObject(filter.post_value) ||
                typeof filter.post_value === 'boolean'
            ) {
                searchFilters[filter.key] = filter.post_value
            }
        })
        setTableState({
            type: stateTypes.UPDATE_STATE_FIELD,
            field: 'filters',
            payload: searchFilters,
        })
    }

    return {
        checkboxHandler,
        clearSelectAll,
        clearStateField,
        handleRequestSort,
        onSelectAllHandler,
        pageChangeHandler,
        rowsPerPageHandler,
        setEditRow,
        setTableState,
        stateTypes,
        tableState,
        togglePublishContentModal,
        onTableHeadCellsChange,
        headCellsRef,
        tableInnerRef,
        tableOuterRef,
        tableRowRef,
        fixedStartCol,
        checkboxRef,
    }
}
